import { Alert, Card, Checkbox, LearnMoreLink, NavigationLink, Text } from '@ui/themed';
import { t } from 'i18next';
import {
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../../constants/features';
import { useEnv } from '../../../../context/env-context';
import { useHandlers } from '../../../../context/page-context';
import { useSettings } from '../../../../context/settings-context';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import {
  SettingPaymentMethodsSchema,
  type SettingPaymentMethodsState,
} from '../../types/customer-group.schema';
import { ResetSettingsButton } from './reset-settings-button';

type PaymentMethodKey = keyof SettingPaymentMethodsState;

export function PaymentMethodsCard() {
  const form = useCustomerGroupBuilderForm();
  const settings = useSettings();
  const env = useEnv();

  const isQuotingEngineEnabled = settings.quotingEnabled;
  const isUsingLegacyRequestForQuotingEnabled = settings.allowLegacyRequestForQuotePaymentMethod;

  const isLegacyQuotingEnabled = !isQuotingEngineEnabled && isUsingLegacyRequestForQuotingEnabled;
  const isQuotingEngineRestricted = featureRestricted(
    features.QUOTING,
    getActiveBillingPlanKey(env?.billing.plan_key),
  );

  const { navigationHandler } = useHandlers();

  const paymentMethodKeys = Object.keys(SettingPaymentMethodsSchema.shape).filter(
    (key) => key !== 'quote' || isLegacyQuotingEnabled,
  ) as PaymentMethodKey[];

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="payment">
        {SETTING_KEY_TO_LABEL.allowedPaymentMethods}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.payment_methods_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/customers#_kdhT" />
        </Text>
        {paymentMethodKeys.map((key) => (
          <form.Field key={key} name={`settings.allowedPaymentMethods.${key}`}>
            {(field) => (
              <Checkbox
                label={t(
                  `features:customer_group.components.payment_methods_card.methods.${key}.label`,
                )}
                checked={!!field.state.value}
                onChange={() => field.handleChange((prev) => !prev)}
                description={t(
                  `features:customer_group.components.payment_methods_card.methods.${key}.description`,
                )}
              />
            )}
          </form.Field>
        ))}
        {isLegacyQuotingEnabled && (
          <Alert
            variant="info"
            title={t(
              `features:customer_group.components.payment_methods_card.methods.quote.alert.${isQuotingEngineRestricted ? 'restricted' : 'not_enabled'}.title`,
            )}
            headingLevel="h3"
          >
            <div className="flex flex-col items-start gap-2">
              <span>
                {t(
                  `features:customer_group.components.payment_methods_card.methods.quote.alert.${isQuotingEngineRestricted ? 'restricted' : 'not_enabled'}.description`,
                )}
              </span>
              <NavigationLink
                onClick={() =>
                  isQuotingEngineRestricted
                    ? navigationHandler.navigateById('plan', {
                        newTab: true,
                      })
                    : navigationHandler.navigate('/configuration/general', {
                        openInDashboard: true,
                        newTab: true,
                      })
                }
              >
                {t(isQuotingEngineRestricted ? 'links:upgrade_plan' : 'links:go_to_settings')}
              </NavigationLink>
            </div>
          </Alert>
        )}

        <div className="flex justify-start">
          <form.Field name="settings.allowedPaymentMethods">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
